<template>
    <modal
        :id="modalId"
        title="Occupancy Taxes"
        size="xl"
        cancel-title="Close"
        ok-title="Set"
        body-class="occupancy-taxes__modal"
        :ok-disabled="loading"
        @ok.prevent="startSet"
        @hide="hideModal"
        @shown="handleModalShown"
    >
        <busy-modal :show="loading"></busy-modal>
        <div class="occupancy-taxes__root">
            <div class="occupancy-taxes__toolbar">
                <b-button
                    class="align-baseline"
                    title="Add Occupancy Tax"
                    variant="default"
                    @click="handleAddOccupancyTaxClick"
                >
                    <font-awesome-icon
                        :icon="['fas', 'plus']"
                    ></font-awesome-icon>
                </b-button>
                <fa-icon-tooltip
                    id="occupancy-taxes-info"
                    :icon="['fas', 'circle-info']"
                    class="text-primary"
                    variant="info"
                >
                    <p style="text-align: left">
                        Select occupancy taxes using checkboxes and then click
                        on "Set" button to apply selected taxes to
                        {{ propertyCount }}
                        {{ propertyCount === 1 ? 'property' : 'properties' }}.
                    </p>
                </fa-icon-tooltip>
            </div>
            <grid
                ref="grid"
                class="occupancy-taxes__grid"
                row-selection="multiple"
                :column-defs="columnsDefs"
                :is-search="false"
                :is-auto-width="true"
                :auto-width-exclude="['amount_display']"
                :row-data="occupancyTaxes"
                :suppress-row-click-selection="true"
                @selection-changed="handleSelectionChange"
            ></grid>
        </div>
        <occupancy-tax-details-modal-form
            :modal-id="occupancyTaxCreateModalID"
            :key="occupancyTaxCreateModalKey"
            :create="createOccupancyTax"
            @success-save="load"
        />
        <occupancy-tax-details-modal-form
            :modal-id="occupancyTaxUpdateModalID"
            :initial-values="currentOccupancyTaxInitialValues"
            :update="updateOccupancyTax"
            @success-save="load"
        />
        <modal
            :id="occupancyTaxesSetConfirmModalID"
            title="Please, confirm"
            ok-title="Yes"
            @ok.prevent="set"
            @hide="hideModal"
        >
            <span v-if="selectedOccupancyTaxes.length > 0">
                Are you sure you want to apply selected taxes to
                {{ propertyCount }} properties?
            </span>
            <span v-else>
                Are you sure you want to reset taxes for
                {{ propertyCount }} properties?
            </span>
        </modal>
    </modal>
</template>

<script>
import API from '../../../../api';
import {
    PARTNER_AIRBNB_OCCUPANCY_TAX_LIST_ENDPOINT,
    PARTNER_AIRBNB_OCCUPANCY_TAX_BY_ID_ENDPOINT,
} from '../../../../endpoints';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import FaIconTooltip from '../../../generic/fa-icon-tooltip/fa-icon-tooltip.vue';
import Grid from '../../../generic/grid/grid.vue';
import Modal from '../../../generic/modal/modal.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import OccupancyTaxDetailsModalForm from './occupancy-tax-details-modal-form.vue';

export default {
    name: 'AirbnbOccupancyTaxesModal',
    components: {
        BusyModal,
        FaIconTooltip,
        Grid,
        Modal,
        ResponsiveButton,
        OccupancyTaxDetailsModalForm,
        actionsCellRenderer: ActionsCellRenderer,
    },
    props: {
        modalId: String,
        defaultOccupancyTaxIds: {
            type: Array,
            default: () => [],
        },
        propertyCount: {
            type: Number,
            default: 0,
        },
        setOccupancyTaxes: {
            type: Function,
            default: () => Promise.resolve(),
        },
    },
    mounted() {
        this.load();
    },
    computed: {
        currentOccupancyTaxInitialValues() {
            if (!!this.currentOccupancyTax) {
                return {
                    id: this.currentOccupancyTax.id,
                    name: this.currentOccupancyTax.name,
                    tax_type: this.currentOccupancyTax.tax_type,
                    amount: this.currentOccupancyTax.amount,
                    amount_type: this.currentOccupancyTax.amount_type,
                    taxable_bases: this.currentOccupancyTax.taxable_bases.map(
                        (taxableBase) => taxableBase.base
                    ),
                    business_tax_id: this.currentOccupancyTax.business_tax_id,
                    tot_registration_id:
                        this.currentOccupancyTax.tot_registration_id,
                    long_term_stay_exemption:
                        this.currentOccupancyTax.long_term_stay_exemption,
                    only_first_nights_exemption:
                        this.currentOccupancyTax.only_first_nights_exemption,
                    max_cap_per_person_per_night:
                        this.currentOccupancyTax.max_cap_per_person_per_night,
                    attestation: true,
                };
            }
        },
    },
    data() {
        return {
            loading: false,
            currentOccupancyTax: null,
            occupancyTaxCreateModalID: 'occupancy-tax-create-modal',
            occupancyTaxCreateModalKey: 0,
            occupancyTaxUpdateModalID: 'occupancy-tax-update-modal',
            occupancyTaxesSetConfirmModalID:
                'occupancy-taxes-set-confirm-modal',
            occupancyTaxes: [],
            selectedOccupancyTaxes: [],
            columnsDefs: [
                {
                    field: 'name',
                    headerName: 'Name',
                    checkboxSelection: true,
                },
                {
                    field: 'tax_type_display',
                    headerName: 'Tax Type',
                },
                {
                    field: 'amount_display',
                    headerName: 'Amount',
                    cellClass: 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                },
                {
                    field: 'amount_type_display',
                    headerName: 'Amount Type',
                },
                {
                    field: 'business_tax_id',
                    headerName: 'Business Tax ID',
                },
                {
                    field: 'tot_registration_id',
                    headerName: 'TOT Registration ID',
                    minWidth: 130,
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        update: (occupancyTax) => {
                            this.currentOccupancyTax = occupancyTax;
                            this.$bvModal.show(this.occupancyTaxUpdateModalID);
                        },
                        delete: async (occupancyTax) => {
                            try {
                                await API.delete(
                                    PARTNER_AIRBNB_OCCUPANCY_TAX_BY_ID_ENDPOINT(
                                        occupancyTax.id
                                    )
                                );
                            } catch (error) {
                                this.errorToast({
                                    text: `Could not delete occupancy tax.`,
                                });
                                return;
                            }
                            this.load();
                        },
                    },
                },
            ],
        };
    },
    methods: {
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        handleAddOccupancyTaxClick() {
            this.$bvModal.show(this.occupancyTaxCreateModalID);
        },
        createOccupancyTax(payload) {
            return API.post(
                PARTNER_AIRBNB_OCCUPANCY_TAX_LIST_ENDPOINT(),
                payload
            );
        },
        updateOccupancyTax({ id, ...payload }) {
            return API.put(
                PARTNER_AIRBNB_OCCUPANCY_TAX_BY_ID_ENDPOINT(id),
                payload
            );
        },
        handleModalShown() {
            this.$refs.grid.gridOptions.api.forEachNode((node) => {
                if (this.defaultOccupancyTaxIds.includes(node.data.id)) {
                    node.setSelected(true);
                } else {
                    node.setSelected(false);
                }
            });
        },
        handleSelectionChange() {
            const gridApi = this.$refs.grid.gridOptions.api;
            this.selectedOccupancyTaxes = gridApi.getSelectedRows();
        },
        async load() {
            this.loading = true;
            try {
                await this.fetchOccupancyTaxes();
            } finally {
                this.loading = false;
            }
        },
        async fetchOccupancyTaxes() {
            try {
                const { data } = await API.get(
                    PARTNER_AIRBNB_OCCUPANCY_TAX_LIST_ENDPOINT()
                );
                this.occupancyTaxes = data;
            } catch {
                this.errorToast({ text: 'Could not load occupancy taxes.' });
            }
        },
        startSet() {
            if (this.propertyCount > 1) {
                this.$bvModal.show(this.occupancyTaxesSetConfirmModalID);
                return;
            }
            this.set();
        },
        async set() {
            this.loading = true;

            try {
                await this.setOccupancyTaxes(
                    this.selectedOccupancyTaxes.map((tax) => tax.id)
                );
                this.$emit('success-set-occupancy-taxes');
                this.hideModal();
            } catch {
                this.errorToast({ text: 'Could not set occupancy taxes.' });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss">
.occupancy-taxes {
    &__modal {
        height: 500px;
    }

    &__root {
        display: flex;
        height: 100%;
    }

    &__toolbar {
        border-right: 1px solid #dee2e6;
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        padding-right: 5px;
    }

    &__grid {
        width: 100%;
    }
}

#occupancy-taxes-info {
    justify-content: center;
    margin-top: 0.5rem;
}
</style>
